
// AgentFocusModal.jsx
// Place in src/components/AgentFocusModal.jsx
//
// Full-viewport focus modal for distraction-free agent conversation.
// Fixes:
//   1. zIndex: 1400 — above MUI AppBar (1100), Drawer (1200), Dialog (1300)
//   2. Input row, toolbar, and all tools passed as render props from
//      AgentDashboard so all voice/attach/math/dictate handlers work intact.
//
// Layer 1 addition: ⓘ icon in header opens AgentDevProfilePanel —
//   shows the user their development profile, outreach controls, and
//   a delete option. Requires two new props from AgentDashboard:
//     uid       — the logged-in user's Firebase uid
//     agentId   — the agentRegistry key (e.g. "livewell", "sales")
//
// Usage in AgentDashboard renderChatPanel() — replace existing modal block:
//
//   {this.state.showFocusModal && this.state.activeAgent && (
//     <AgentFocusModal
//       agent={this.state.activeAgent}
//       messages={this.state.messages}
//       loading={this.state.loading}
//       blocked={this.state.blocked}
//       blockReason={this.state.blockReason}
//       blockMessage={this.state.blockMessage}
//       onClose={() => this.setState({ showFocusModal: false })}
//       renderToolbar={this.renderChatToolbar}
//       renderInputRow={this.renderChatInputRow}
//       renderAttachment={this.renderChatAttachment}
//       renderMathKeyboard={this.renderChatMathKeyboard}
//       uid={this.props.userdata?.userID || this.props.uid}
//       agentId={this.state.activeAgent?.id}
//     />
//   )}

import React, { useRef, useEffect, useState } from 'react';
import MathRenderer from './MathRenderer';
import AudioBubble from './AudioBubble';
import AgentDevProfilePanel from './AgentDevProfilePanel';
//import videourl from './DennisImg/welcome_message.mp4'

const FONT          = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
const FONT_AI       = "'Space Grotesk', 'Segoe UI', sans-serif";
const BG_CHAT       = '#f4f8fd';
const BG_HEADER     = '#dce8f5';
const BG_BUBBLE_USR = '#1a6fba';
const BG_BUBBLE_AGT = '#ffffff';
const BORDER        = 'rgba(26,111,186,0.15)';
const BORDER_A      = 'rgba(26,111,186,0.45)';
const TEXT_H        = '#0d2b4a';
const TEXT_BODY     = '#1a3a5c';
const TEXT_MID      = '#3a6080';
const TEXT_MUTE     = '#7a9bb5';
const ACCENT        = '#1a6fba';
const ACCENT_F      = 'rgba(26,111,186,0.10)';

function useIsMobile(breakpoint = 480) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export default function AgentFocusModal({
  agent,
  welcome,
  messages,
  loading,
  showTypingIndicator,  
  blocked,
  blockReason,
  blockMessage,
  onClose,
  renderToolbar,
  renderInputRow,
  renderAttachment,
  renderMathKeyboard,
  renderBlockedBanner,
  onMessageAutoplayed,
  uid,
  agentId,
}) {

  console.log('AgentFocusModal props — uid:', uid, 'agentId:', agentId);

  const messagesEndRef = useRef(null);
  const [showDevProfile, setShowDevProfile] = useState(false);
  const isMobile = useIsMobile();

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape' && !showDevProfile) onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, showDevProfile]);


// ─── TypingIndicator — animated dots with random "thinking" pauses ──────────
const TypingIndicator = () => {
  const [isPaused, setIsPaused] = React.useState(false);

  React.useEffect(() => {
    // Randomly pause the animation to feel like the agent is thinking
    let timeoutId;
    const cycle = () => {
      // Animate for 2–4 seconds
      const animateDuration = 2000 + Math.random() * 2000;
      setIsPaused(false);
      timeoutId = setTimeout(() => {
        // Pause for 0.6–1.8 seconds
        const pauseDuration = 600 + Math.random() * 1200;
        setIsPaused(true);
        timeoutId = setTimeout(cycle, pauseDuration);
      }, animateDuration);
    };
    cycle();
    return () => clearTimeout(timeoutId);
  }, []);

  const dotBase = {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#7a9bb5',
    display: 'inline-block',
    margin: '0 3px',
    animation: isPaused
      ? 'none'
      : 'typingBob 1.2s infinite ease-in-out',
  };

  return (
    <>
      {/* Keyframes injected once — safe to include multiple times */}
      <style>{`
        @keyframes typingBob {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-5px);
            opacity: 1;
          }
        }
      `}</style>
      <div style={{
        alignSelf: 'flex-start',
        padding: '12px 16px',
        borderRadius: '12px 12px 12px 2px',
        backgroundColor: '#ffffff',
        border: '1px solid rgba(26,111,186,0.15)',
        display: 'inline-flex',
        alignItems: 'center',
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
      }}>
        <span style={{ ...dotBase, animationDelay: '0s' }} />
        <span style={{ ...dotBase, animationDelay: '0.2s' }} />
        <span style={{ ...dotBase, animationDelay: '0.4s' }} />
      </div>
    </>
  );
};

  const [avatarError, setAvatarError] = useState(false);

  const renderIcon = (sizePx, style = {}) => {
    if (agent.avatar && !avatarError) {
      return (
        <img
          src={agent.avatar}
          alt={agent.label}
          onError={() => setAvatarError(true)}
          style={{ width: sizePx, height: sizePx, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, display: 'block', ...style }}
        />
      );
    }
    return <span style={{ fontSize: sizePx, lineHeight: 1, ...style }}>{agent.icon}</span>;
  };

const renderMessages = () => {
  if (messages.length === 0 && !loading && !blocked) {
    if (!welcome) {
      return (
        <div style={S.emptyChat}>
          {renderIcon(44)}
          <div style={S.emptyChatText}>
            Start the conversation with {agent.label} whenever you're ready.
          </div>
        </div>
      );
    }
    return (
      <>
        <div style={S.bubble('assistant', isMobile)}>
          <MathRenderer text={welcome.text} />
        </div>
        <div style={{ ...S.bubble('assistant', isMobile), width: '240px', maxWidth: 'none', minWidth: 'unset', overflowX: 'visible' }}>
          <AudioBubble
            audioUrl={welcome.audio}
            isUser={false}
            autoPlay={false}
            onAutoPlayed={() => {}}
          />
        </div>
        <div ref={messagesEndRef} />
      </>
    );
  }
  return (
    <>
      {messages.map((msg, i) => {
        const isUser = msg.role === 'user';

        if (msg.wasVoice || msg.audioPath) {
          return (
            <div key={i} style={{ ...S.bubble(msg.role, isMobile), width: '240px', maxWidth: 'none', minWidth: 'unset', overflowX: 'visible' }}>
              <AudioBubble
                audioUrl={msg.audioUrl}
                isUser={isUser}
                autoPlay={msg.autoPlay === true}
                onAutoPlayed={() => onMessageAutoplayed && onMessageAutoplayed(i)}
              />
            </div>
          );
        }

        return (
          <div key={i} style={S.bubble(msg.role, isMobile)}>
            <MathRenderer text={msg.content} />
          </div>
        );
      })}
      {loading && showTypingIndicator && <TypingIndicator />}
      {blocked && renderBlockedBanner && renderBlockedBanner()}
      <div ref={messagesEndRef} />
    </>
  );
};

  return (
    <div
      style={S.backdrop}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
<div style={S.modal(isMobile)}>

        {/* ── Header ── */}
        <div style={S.header(isMobile)}>
          <div style={S.headerLeft}>
          {renderIcon(52)}
            <div>
              <div style={S.agentName}>{agent.label}</div>
              <div style={S.focusLabel}>Focus Mode</div>
            </div>
          </div>

          {/* ── Header right: profile button + close ── */}
          <div style={S.headerRight}>
            {/* Dev profile button — only shown when uid + agentId are available */}

          {uid && agentId && (
            <button
              style={isMobile ? S.profileBtnIcon : S.profileBtn}
              onClick={() => setShowDevProfile(true)}
              title="View your development profile"
            >
              {isMobile ? 'ⓘ' : 'ⓘ Profile'}
            </button>
          )}
        <button
          style={isMobile ? S.closeBtnIcon : S.closeBtn}
          onClick={onClose}
          title="Close focus mode (Esc)"
        >
          {isMobile ? '✕' : '✕ Close'}
        </button>
          </div>
        </div>

       {/* ── Messages ── */}
        <div style={S.messages(isMobile)}>
          {renderMessages()}
        </div>

        {/* ── Input section ── */}
        <div style={S.inputSection}>
          {renderToolbar    && <div style={S.toolbarWrapper}>{renderToolbar()}</div>}
          {renderAttachment && renderAttachment()}
          {renderMathKeyboard && renderMathKeyboard()}
          {renderInputRow   && renderInputRow()}
        </div>

      </div>

      {/* ── Dev profile panel — rendered outside modal div so it sits above ── */}

      {showDevProfile && uid && agentId && (
        <AgentDevProfilePanel
          open={showDevProfile}
          onClose={() => setShowDevProfile(false)}
          uid={uid}
          agentId={agentId}
          agentLabel={agent.label}
        />
      )}

    </div>
  );
}

const S = {
  backdrop: {
    position:        'fixed',
    top:             0,
    left:            0,
    right:           0,
    bottom:          0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    zIndex:          1400,
    display:         'flex',
    alignItems:      'stretch',
    justifyContent:  'center',
    padding:         '0',
  },
modal: (isMobile) => ({
    display:         'flex',
    flexDirection:   'column',
    width:           '100%',
    maxWidth:        isMobile ? '100%' : '900px',
    backgroundColor: BG_CHAT,
    boxShadow:       isMobile ? 'none' : '0 24px 80px rgba(0,0,0,0.35)',
    overflow:        'hidden',
    margin:          isMobile ? '0' : '20px',
    borderRadius:    isMobile ? '0' : '12px',
    maxHeight:       isMobile ? '100vh' : 'calc(100vh - 40px)',
    height:          isMobile ? '100vh' : 'auto', // fallback for older browsers
    ...(isMobile ? { height: '100dvh', maxHeight: '100dvh' } : {}),
  }),
header: (isMobile) => ({
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         isMobile ? '10px 14px' : '14px 20px',
    backgroundColor: BG_HEADER,
    borderBottom:    `1px solid ${BORDER}`,
    flexShrink:      0,
    gap:             '12px',
  }),
  headerLeft: {
    display:    'flex',
    alignItems: 'center',
    gap:        '10px',
    minWidth:   0,
    flex:       1,       
    overflow:   'hidden',
  },
  headerRight: {
    display:    'flex',
    alignItems: 'center',
    gap:        '8px',
    flexShrink: 0,
  },
  agentName: {
    fontSize:     '15px',
    fontWeight:   '700',
    color:        TEXT_H,
    fontFamily:   FONT_AI,
    letterSpacing: '-0.3px',
    whiteSpace:   'nowrap',
    overflow:     'hidden',
    textOverflow: 'ellipsis',
  },
  focusLabel: {
    fontSize:      '10px',
    color:         ACCENT,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    fontWeight:    '600',
    fontFamily:    FONT,
  },
  profileBtn: {
    padding:         '8px 14px',
    border:          `1px solid ${BORDER}`,
    borderRadius:    '6px',
    backgroundColor: 'transparent',
    color:           ACCENT,
    cursor:          'pointer',
    fontSize:        '12px',
    fontFamily:      FONT,
    whiteSpace:      'nowrap',
    transition:      'all 0.15s',
  },
  closeBtn: {
    padding:         '8px 16px',
    border:          `1px solid ${BORDER}`,
    borderRadius:    '6px',
    backgroundColor: 'transparent',
    color:           TEXT_MID,
    cursor:          'pointer',
    fontSize:        '12px',
    fontFamily:      FONT,
    whiteSpace:      'nowrap',
    transition:      'all 0.15s',
  },
  messages: (isMobile) => ({
    flex:          1,
    overflowY:     'auto',
    padding:       isMobile ? '14px 10px' : '20px 24px',
    display:       'flex',
    flexDirection: 'column',
    gap:           isMobile ? '10px' : '14px',
    minHeight:     0,
  }),
  bubble: (role, isMobile) => ({
    maxWidth:        isMobile
      ? (role === 'user' ? '88%' : '92%')
      : (role === 'user' ? '78%' : '88%'),
    alignSelf:       role === 'user' ? 'flex-end' : 'flex-start',
    padding:         isMobile ? '10px 13px' : '12px 16px',
    borderRadius:    role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
    backgroundColor: role === 'user' ? BG_BUBBLE_USR : BG_BUBBLE_AGT,
    border:          `1px solid ${role === 'user' ? 'transparent' : BORDER}`,
    fontSize:        isMobile ? '14.5px' : '15px',
    lineHeight:      isMobile ? '1.6' : '1.7',
    color:           role === 'user' ? '#ffffff' : TEXT_BODY,
    fontFamily:      FONT,
    boxShadow:       '0 1px 4px rgba(0,0,0,0.07)',
  }),
  typingIndicator: {
    alignSelf:       'flex-start',
    padding:         '12px 16px',
    borderRadius:    '14px 14px 14px 3px',
    backgroundColor: BG_BUBBLE_AGT,
    border:          `1px solid ${BORDER}`,
    color:           TEXT_MUTE,
    fontSize:        '20px',
    letterSpacing:   '4px',
  },
  emptyChat: {
    flex:           1,
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    color:          TEXT_MUTE,
    textAlign:      'center',
    padding:        '60px 40px',
    gap:            '14px',
  },
  emptyChatText: {
    fontSize:   '13px',
    lineHeight: '1.8',
    fontStyle:  'italic',
    fontFamily: FONT,
    color:      TEXT_MID,
    maxWidth:   '320px',
  },
  blockedBanner: {
    padding:         '14px 18px',
    border:          `1px solid ${BORDER_A}`,
    borderRadius:    '8px',
    backgroundColor: ACCENT_F,
    fontSize:        '13px',
    color:           ACCENT,
    lineHeight:      '1.7',
    textAlign:       'center',
    fontFamily:      FONT,
  },
  inputSection: {
    borderTop:       `1px solid ${BORDER}`,
    backgroundColor: BG_HEADER,
    flexShrink:      0,
  },
  toolbarWrapper: {},
  profileBtnIcon: {
  width:           '32px',
  height:          '32px',
  padding:         0,
  border:          `1px solid ${BORDER}`,
  borderRadius:    '6px',
  backgroundColor: 'transparent',
  color:           ACCENT,
  cursor:          'pointer',
  fontSize:        '15px',
  fontFamily:      FONT,
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  flexShrink:      0,
},
closeBtnIcon: {
  width:           '32px',
  height:          '32px',
  padding:         0,
  border:          `1px solid ${BORDER}`,
  borderRadius:    '6px',
  backgroundColor: 'transparent',
  color:           TEXT_MID,
  cursor:          'pointer',
  fontSize:        '15px',
  fontFamily:      FONT,
  display:         'flex',
  alignItems:      'center',
  justifyContent:  'center',
  flexShrink:      0,
},
messages: (isMobile) => ({
  flex:          1,
  overflowY:     'auto',
  padding:       isMobile ? '14px 10px' : '20px 24px',
  display:       'flex',
  flexDirection: 'column',
  gap:           isMobile ? '10px' : '14px',
  minHeight:     0,
}),
};
/////////////////////////////////////////////////////////////////////////////////////////////
// // AgentFocusModal.jsx
// // Place in src/components/AgentFocusModal.jsx
// //
// // Full-viewport focus modal for distraction-free agent conversation.
// // Fixes:
// //   1. zIndex: 1400 — above MUI AppBar (1100), Drawer (1200), Dialog (1300)
// //   2. Input row, toolbar, and all tools passed as render props from
// //      AgentDashboard so all voice/attach/math/dictate handlers work intact.
// //
// // Usage in AgentDashboard renderChatPanel() — replace existing modal block:
// //
// //   {this.state.showFocusModal && this.state.activeAgent && (
// //     <AgentFocusModal
// //       agent={this.state.activeAgent}
// //       messages={this.state.messages}
// //       loading={this.state.loading}
// //       blocked={this.state.blocked}
// //       blockReason={this.state.blockReason}
// //       blockMessage={this.state.blockMessage}
// //       onClose={() => this.setState({ showFocusModal: false })}
// //       renderToolbar={this.renderChatToolbar}
// //       renderInputRow={this.renderChatInputRow}
// //       renderAttachment={this.renderChatAttachment}
// //       renderMathKeyboard={this.renderChatMathKeyboard}
// //     />
// //   )}

// import React, { useRef, useEffect } from 'react';
// import MathRenderer from './MathRenderer';
// import AudioBubble from './AudioBubble';

// const FONT          = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
// const BG_CHAT       = '#f4f8fd';
// const BG_HEADER     = '#dce8f5';
// const BG_BUBBLE_USR = '#1a6fba';
// const BG_BUBBLE_AGT = '#ffffff';
// const BORDER        = 'rgba(26,111,186,0.15)';
// const BORDER_A      = 'rgba(26,111,186,0.45)';
// const TEXT_H        = '#0d2b4a';
// const TEXT_BODY     = '#1a3a5c';
// const TEXT_MID      = '#3a6080';
// const TEXT_MUTE     = '#7a9bb5';
// const ACCENT        = '#1a6fba';
// const ACCENT_F      = 'rgba(26,111,186,0.10)';

// export default function AgentFocusModal({
//   agent,
//   messages,
//   loading,
//   blocked,
//   blockReason,
//   blockMessage,
//   onClose,
//   renderToolbar,
//   renderInputRow,
//   renderAttachment,
//   renderMathKeyboard,
// }) {
//   const messagesEndRef = useRef(null);

//   useEffect(() => {
//     messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
//   }, [messages, loading]);

//   useEffect(() => {
//     const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
//     window.addEventListener('keydown', handleKey);
//     return () => window.removeEventListener('keydown', handleKey);
//   }, [onClose]);

//   const renderMessages = () => {
//     if (messages.length === 0 && !loading && !blocked) {
//       return (
//         <div style={S.emptyChat}>
//           <span style={{ fontSize: '44px' }}>{agent.icon}</span>
//           <div style={S.emptyChatText}>
//             Begin your conversation.<br />
//             Every question draws wisdom out.
//           </div>
//         </div>
//       );
//     }
//     return (
//       <>
//       {messages.map((msg, i) => {
//         const isUser = msg.role === 'user';

//         if (msg.wasVoice || msg.audioPath) {
//           // Voice bubble — fixed width for audio player, no maxWidth constraint
//           return (
//             <div
//               key={i}
//               style={{
//                 ...S.bubble(msg.role),
//                 width: '240px',
//                 maxWidth: 'none',
//                 minWidth: 'unset',
//                 overflowX: 'visible',
//               }}
//             >
//               <AudioBubble
//                 audioUrl={msg.audioUrl}
//                 isUser={isUser}
//                 autoPlay={false}
//               />
//             </div>
//           );
//         }

//         // Text bubble — normal maxWidth, no fixed width, MathRenderer for LaTeX
//         return (
//           <div key={i} style={S.bubble(msg.role)}>
//             <MathRenderer text={msg.content} />
//           </div>
//         );
//       })}
//         {loading && <div style={S.typingIndicator}>···</div>}
//         {blocked && (
//           <div style={S.blockedBanner}>
//             {blockMessage}
//             {blockReason !== 'unsubscribed' && (
//               <div style={{ marginTop: '8px' }}>
//                 <a
//                   href="https://www.patreon.com/mevision"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   style={{ color: ACCENT, textDecoration: 'underline', fontWeight: '600' }}
//                 >
//                   Upgrade your pledge →
//                 </a>
//               </div>
//             )}
//           </div>
//         )}
//         <div ref={messagesEndRef} />
//       </>
//     );
//   };

//   return (
//     <div
//       style={S.backdrop}
//       onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
//     >
//       <div style={S.modal}>

//         {/* ── Header ── */}
//         <div style={S.header}>
//           <div style={S.headerLeft}>
//             <span style={{ fontSize: '22px', lineHeight: 1 }}>{agent.icon}</span>
//             <div>
//               <div style={S.agentName}>{agent.label}</div>
//               <div style={S.focusLabel}>Focus Mode</div>
//             </div>
//           </div>
//           <button style={S.closeBtn} onClick={onClose} title="Close focus mode (Esc)">
//             ✕ Close
//           </button>
//         </div>

//         {/* ── Messages ── */}
//         <div style={S.messages}>
//           {renderMessages()}
//         </div>

//         {/* ── Input section ── */}
//         {/* All blocks are render props from AgentDashboard.
//             Their event handlers, state, refs, and logic are fully intact —
//             the modal is purely a layout shell. */}
//         <div style={S.inputSection}>
//           {renderToolbar    && <div style={S.toolbarWrapper}>{renderToolbar()}</div>}
//           {renderAttachment && renderAttachment()}
//           {renderMathKeyboard && renderMathKeyboard()}
//           {renderInputRow   && renderInputRow()}
//         </div>

//       </div>
//     </div>
//   );
// }

// const S = {
//   backdrop: {
//     position:        'fixed',
//     top:             0,
//     left:            0,
//     right:           0,
//     bottom:          0,
//     backgroundColor: 'rgba(0,0,0,0.6)',
//     zIndex:          1400,   // above MUI AppBar(1100) Drawer(1200) Dialog(1300)
//     display:         'flex',
//     alignItems:      'stretch',
//     justifyContent:  'center',
//     padding:         '0',
//   },
//   modal: {
//     display:         'flex',
//     flexDirection:   'column',
//     width:           '100%',
//     maxWidth:        '900px',
//     backgroundColor: BG_CHAT,
//     boxShadow:       '0 24px 80px rgba(0,0,0,0.35)',
//     overflow:        'hidden',
//     margin:          '20px',
//     borderRadius:    '12px',
//     maxHeight:       'calc(100vh - 40px)',
//   },
//   header: {
//     display:         'flex',
//     justifyContent:  'space-between',
//     alignItems:      'center',
//     padding:         '14px 20px',
//     backgroundColor: BG_HEADER,
//     borderBottom:    `1px solid ${BORDER}`,
//     flexShrink:      0,
//     gap:             '12px',
//   },
//   headerLeft: {
//     display:    'flex',
//     alignItems: 'center',
//     gap:        '10px',
//     minWidth:   0,
//   },
//   agentName: {
//     fontSize:     '15px',
//     fontWeight:   '700',
//     color:        TEXT_H,
//     fontFamily:   FONT,
//     whiteSpace:   'nowrap',
//     overflow:     'hidden',
//     textOverflow: 'ellipsis',
//   },
//   focusLabel: {
//     fontSize:      '10px',
//     color:         ACCENT,
//     letterSpacing: '2px',
//     textTransform: 'uppercase',
//     fontWeight:    '600',
//     fontFamily:    FONT,
//   },
//   closeBtn: {
//     padding:         '8px 16px',
//     border:          `1px solid ${BORDER}`,
//     borderRadius:    '6px',
//     backgroundColor: 'transparent',
//     color:           TEXT_MID,
//     cursor:          'pointer',
//     fontSize:        '12px',
//     fontFamily:      FONT,
//     whiteSpace:      'nowrap',
//     flexShrink:      0,
//     transition:      'all 0.15s',
//   },
//   messages: {
//     flex:          1,
//     overflowY:     'auto',
//     padding:       '20px 24px',
//     display:       'flex',
//     flexDirection: 'column',
//     gap:           '14px',
//     minHeight:     0,
//   },
// bubble: (role) => ({
//   maxWidth:        role === 'user' ? '78%' : '88%',
//   alignSelf:       role === 'user' ? 'flex-end' : 'flex-start',
//   padding:         '12px 16px',
//   borderRadius:    role === 'user' ? '14px 14px 3px 14px' : '14px 14px 14px 3px',
//   backgroundColor: role === 'user' ? BG_BUBBLE_USR : BG_BUBBLE_AGT,
//   border:          `1px solid ${role === 'user' ? 'transparent' : BORDER}`,
//   fontSize:        '15px',
//   lineHeight:      '1.7',
//   color:           role === 'user' ? '#ffffff' : TEXT_BODY,
//   fontFamily:      FONT,
//   boxShadow:       '0 1px 4px rgba(0,0,0,0.07)',
//   // No minWidth, no fixed width, no overflowX override here —
//   // those only apply to voice bubbles via the inline style override above
// }),
//   typingIndicator: {
//     alignSelf:       'flex-start',
//     padding:         '12px 16px',
//     borderRadius:    '14px 14px 14px 3px',
//     backgroundColor: BG_BUBBLE_AGT,
//     border:          `1px solid ${BORDER}`,
//     color:           TEXT_MUTE,
//     fontSize:        '20px',
//     letterSpacing:   '4px',
//   },
//   emptyChat: {
//     flex:           1,
//     display:        'flex',
//     flexDirection:  'column',
//     alignItems:     'center',
//     justifyContent: 'center',
//     color:          TEXT_MUTE,
//     textAlign:      'center',
//     padding:        '60px 40px',
//     gap:            '14px',
//   },
//   emptyChatText: {
//     fontSize:   '13px',
//     lineHeight: '1.8',
//     fontStyle:  'italic',
//     fontFamily: FONT,
//     color:      TEXT_MID,
//     maxWidth:   '320px',
//   },
//   blockedBanner: {
//     padding:         '14px 18px',
//     border:          `1px solid ${BORDER_A}`,
//     borderRadius:    '8px',
//     backgroundColor: ACCENT_F,
//     fontSize:        '13px',
//     color:           ACCENT,
//     lineHeight:      '1.7',
//     textAlign:       'center',
//     fontFamily:      FONT,
//   },
//   inputSection: {
//     borderTop:       `1px solid ${BORDER}`,
//     backgroundColor: BG_HEADER,
//     flexShrink:      0,
//   },
//   toolbarWrapper: {},
// };